/*
 * ConnectorButtons - OAuth login buttons for external connectors
 * (GitHub, Jira/Confluence via Atlassian OAuth)
 *
 * Each button triggers Backstage's built-in OAuth popup for the corresponding
 * provider and stores the resulting access token in the ChatbotApi instance,
 * which forwards it as an HTTP header on every A2A request:
 *
 *   GitHub     → X-GitHub-Token
 *   (future)   → X-Jira-Token / X-Confluence-Token
 */

import { useState, useCallback, useEffect } from 'react';
import { githubAuthApiRef, useApi, IdentityApi } from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  Chip,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import GitHubIcon from '@material-ui/icons/GitHub';
import BusinessIcon from '@material-ui/icons/Business';

// ─── Types ───────────────────────────────────────────────────────────────────

export type ExternalProvider = 'github' | 'atlassian';

interface ConnectorButtonsProps {
  /** ChatbotApi instance – may be null while the agent is still connecting */
  chatbotApi: {
    setExternalToken: (
      provider: ExternalProvider,
      token: string | null,
    ) => void;
  } | null;
  /** Show the GitHub connector button */
  enableGitHub?: boolean;
  /** Show the Atlassian (Jira + Confluence) connector button */
  enableAtlassian?: boolean;
  /** Backstage IdentityApi – used to obtain the bearer token sent to CAIPE */
  identityApi?: IdentityApi;
  /** CAIPE supervisor base URL (e.g. https://caipe.example.com) */
  caipeBaseUrl?: string;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    gap: 8,
    padding: '4px 8px 8px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  connectedChip: {
    backgroundColor: '#238636',
    color: '#fff',
    fontSize: '0.8rem',
    height: 34,
    '& .MuiChip-deleteIcon': {
      color: 'rgba(255,255,255,0.7)',
      '&:hover': { color: '#fff' },
    },
  },
  connectButton: {
    textTransform: 'none',
    fontSize: '0.85rem',
    borderRadius: 16,
    height: 34,
    padding: '0 14px',
  },
}));

// ─── GitHub connector ─────────────────────────────────────────────────────────

/**
 * Inner component – rendered only when GitHub auth provider is configured.
 * Keeps useApi(githubAuthApiRef) at the top level as required by React hooks rules.
 */
function GitHubConnectorButton({
  chatbotApi,
  identityApi,
  caipeBaseUrl,
}: {
  chatbotApi: ConnectorButtonsProps['chatbotApi'];
  identityApi?: IdentityApi;
  caipeBaseUrl?: string;
}) {
  const classes = useStyles();
  const githubApi = useApi(githubAuthApiRef);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Fire-and-forget POST to CAIPE to persist the token in the broker. */
  const persistTokenInBroker = useCallback(async (token: string) => {
    if (!caipeBaseUrl || !identityApi) return;
    try {
      const credentials = await identityApi.getCredentials();
      await fetch(`${caipeBaseUrl}/api/integrations/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(credentials.token ? { Authorization: `Bearer ${credentials.token}` } : {}),
        },
        body: JSON.stringify({ provider: 'github', token }),
      });
    } catch (err) {
      console.warn('[ConnectorButtons] Failed to persist GitHub token in broker:', err);
    }
  }, [caipeBaseUrl, identityApi]);

  /** Fire-and-forget DELETE to CAIPE to revoke the persisted token. */
  const revokeTokenInBroker = useCallback(async () => {
    if (!caipeBaseUrl || !identityApi) return;
    try {
      const credentials = await identityApi.getCredentials();
      await fetch(`${caipeBaseUrl}/api/integrations/token/github`, {
        method: 'DELETE',
        headers: credentials.token ? { Authorization: `Bearer ${credentials.token}` } : {},
      });
    } catch (err) {
      console.warn('[ConnectorButtons] Failed to revoke GitHub token in broker:', err);
    }
  }, [caipeBaseUrl, identityApi]);

  // On mount: check if there is already a persisted token in the broker so the
  // button reflects the real connected state even after a page refresh.
  useEffect(() => {
    if (!caipeBaseUrl || !identityApi) return;
    let cancelled = false;
    (async () => {
      try {
        const credentials = await identityApi.getCredentials();
        const res = await fetch(`${caipeBaseUrl}/api/integrations/token/github`, {
          method: 'GET',
          headers: credentials.token ? { Authorization: `Bearer ${credentials.token}` } : {},
        });
        if (!cancelled && res.ok) {
          const data = await res.json();
          if (data.connected) setConnected(true);
        }
      } catch {
        // silently ignore – broker may not be configured
      }
    })();
    return () => { cancelled = true; };
  }, [caipeBaseUrl, identityApi]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnect = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Opens Backstage OAuth popup for GitHub
      const token = await githubApi.getAccessToken([
        'repo',
        'read:org',
        'read:user',
      ]);
      chatbotApi?.setExternalToken('github', token);
      // Persist token in the CAIPE Token Broker so it survives page refresh
      await persistTokenInBroker(token);
      setConnected(true);
    } catch (err: any) {
      const msg =
        err?.message?.includes('closed') || err?.message?.includes('cancel')
          ? 'GitHub login cancelled.'
          : `GitHub login failed: ${err?.message ?? String(err)}`;
      setError(msg);
      console.error('[ConnectorButtons] GitHub connect error:', err);
    } finally {
      setLoading(false);
    }
  }, [githubApi, chatbotApi, persistTokenInBroker]);

  const handleDisconnect = useCallback(() => {
    chatbotApi?.setExternalToken('github', null);
    // Revoke persisted token from the CAIPE Token Broker
    revokeTokenInBroker();
    setConnected(false);
    setError(null);
    // Sign out of the Backstage GitHub session so next connect triggers a fresh popup
    githubApi.signOut().catch(() => {});
  }, [githubApi, chatbotApi, revokeTokenInBroker]);

  return (
    <>
      {connected ? (
        <Tooltip title="Click to disconnect your GitHub account from the agent">
          <Chip
            icon={<GitHubIcon style={{ fontSize: 16 }} />}
            label="GitHub Connected"
            onDelete={handleDisconnect}
            className={classes.connectedChip}
          />
        </Tooltip>
      ) : (
        <Tooltip title="Connect your GitHub account so the agent uses your personal access permissions">
          <span>
            <Button
              variant="outlined"
              startIcon={<GitHubIcon style={{ fontSize: 16 }} />}
              onClick={handleConnect}
              disabled={loading || !chatbotApi}
              className={classes.connectButton}
            >
              {loading ? 'Connecting…' : 'Connect GitHub'}
            </Button>
          </span>
        </Tooltip>
      )}
      {error && (
        <span style={{ fontSize: '0.7rem', color: '#d32f2f' }}>{error}</span>
      )}
    </>
  );
}
// ─── Atlassian connector ─────────────────────────────────────────────────────────────────

/**
 * Atlassian connector button.
 * Uses a server-side OAuth 3LO flow through CAIPE so the client_secret never
 * reaches the browser. Flow: frontend → GET /api/integrations/atlassian/authorize
 * → popup → Atlassian → CAIPE callback → broker stores token → postMessage → connected.
 */
function AtlassianConnectorButton({
  identityApi,
  caipeBaseUrl,
}: {
  identityApi?: IdentityApi;
  caipeBaseUrl?: string;
}) {
  const classes = useStyles();
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // On mount: check broker for existing token (survives page refresh)
  useEffect(() => {
    if (!caipeBaseUrl || !identityApi) return;
    let cancelled = false;
    (async () => {
      try {
        const credentials = await identityApi.getCredentials();
        const res = await fetch(`${caipeBaseUrl}/api/integrations/token/jira`, {
          method: 'GET',
          headers: credentials.token
            ? { Authorization: `Bearer ${credentials.token}` }
            : {},
        });
        if (!cancelled && res.ok) {
          const data = await res.json();
          if (data.connected) setConnected(true);
        }
      } catch {
        // broker may not be configured – ignore silently
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caipeBaseUrl, identityApi]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnect = useCallback(async () => {
    if (!caipeBaseUrl || !identityApi) return;
    setLoading(true);
    setError(null);
    try {
      const credentials = await identityApi.getCredentials();
      // Ask CAIPE for the Atlassian OAuth URL (server-side, keeps client_secret safe)
      const res = await fetch(`${caipeBaseUrl}/api/integrations/atlassian/authorize`, {
        headers: credentials.token
          ? { Authorization: `Bearer ${credentials.token}` }
          : {},
      });
      if (!res.ok) {
        throw new Error(`Failed to get authorize URL (${res.status})`);
      }
      const { url } = await res.json();

      // Open OAuth popup
      const popup = window.open(
        url,
        'atlassian-oauth',
        'width=600,height=700,left=200,top=100',
      );

      // Listen for postMessage from CAIPE callback page
      const handler = (event: MessageEvent) => {
        if (!event.data?.atlassianOAuth) return;
        window.removeEventListener('message', handler);
        popup?.close();
        if (event.data.atlassianOAuth.success) {
          setConnected(true);
        } else {
          setError(
            `Atlassian login failed: ${event.data.atlassianOAuth.message}`,
          );
        }
        setLoading(false);
      };
      window.addEventListener('message', handler);

      // Timeout: if popup closed without message (user closed manually)
      const pollClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(pollClosed);
          window.removeEventListener('message', handler);
          setLoading(false);
        }
      }, 500);
    } catch (err: any) {
      setError(`Atlassian login failed: ${err?.message ?? String(err)}`);
      setLoading(false);
    }
  }, [caipeBaseUrl, identityApi]);

  const handleDisconnect = useCallback(async () => {
    if (!caipeBaseUrl || !identityApi) return;
    try {
      const credentials = await identityApi.getCredentials();
      await fetch(`${caipeBaseUrl}/api/integrations/atlassian/disconnect`, {
        method: 'DELETE',
        headers: credentials.token
          ? { Authorization: `Bearer ${credentials.token}` }
          : {},
      });
    } catch (err) {
      console.warn('[ConnectorButtons] Failed to revoke Atlassian token:', err);
    }
    setConnected(false);
    setError(null);
  }, [caipeBaseUrl, identityApi]);

  return (
    <>
      {connected ? (
        <Tooltip title="Click to disconnect your Atlassian account (Jira + Confluence) from the agent">
          <Chip
            icon={<BusinessIcon style={{ fontSize: 16 }} />}
            label="Atlassian Connected"
            onDelete={handleDisconnect}
            className={classes.connectedChip}
          />
        </Tooltip>
      ) : (
        <Tooltip title="Connect your Atlassian account so the agent uses your personal Jira &amp; Confluence permissions">
          <span>
            <Button
              variant="outlined"
              startIcon={<BusinessIcon style={{ fontSize: 16 }} />}
              onClick={handleConnect}
              disabled={loading || !caipeBaseUrl}
              className={classes.connectButton}
            >
              {loading ? 'Connecting…' : 'Connect Atlassian'}
            </Button>
          </span>
        </Tooltip>
      )}
      {error && (
        <span style={{ fontSize: '0.7rem', color: '#d32f2f' }}>{error}</span>
      )}
    </>
  );
}
// ─── Public component ─────────────────────────────────────────────────────────

/**
 * ConnectorButtons
 *
 * Renders OAuth login/logout buttons for external connectors.
 * Only shows a connector button when the corresponding `enable*` prop is true
 * (which should match having the auth provider configured in app-config.yaml).
 *
 * Returns null when no connectors are enabled.
 */
export function ConnectorButtons({
  chatbotApi,
  enableGitHub = false,
  enableAtlassian = false,
  identityApi,
  caipeBaseUrl,
}: ConnectorButtonsProps) {
  const classes = useStyles();

  if (!enableGitHub && !enableAtlassian) return null;

  return (
    <Box className={classes.root}>
      {enableGitHub && (
        <GitHubConnectorButton
          chatbotApi={chatbotApi}
          identityApi={identityApi}
          caipeBaseUrl={caipeBaseUrl}
        />
      )}
      {enableAtlassian && (
        <AtlassianConnectorButton
          identityApi={identityApi}
          caipeBaseUrl={caipeBaseUrl}
        />
      )}
    </Box>
  );
}
